import React, { useEffect, useRef, useState } from "react";
import { BaseSavedSearch, Category } from "../model/model";
import { useSavedSearchService } from "../services/useSavedSearchService";
import { SavedSearchModal } from "./modal/SavedSearchModal";
import { Loader } from "./error/Loader";
import { SettingsError } from "./error/SettingsError";

export interface Props {
  isOpen: boolean;
  closeModal: () => void;
  savedSearchId: number;
  category: Category;
  children: (
    search: BaseSavedSearch,
    isSettingsModalOpen: boolean,
    closeSettingsModal: () => void
  ) => JSX.Element;
}

export const SavedSearchProvider = ({
  isOpen,
  closeModal,
  savedSearchId,
  category,
  children,
}: Props): JSX.Element => {
  const savedSearchService = useSavedSearchService(category);
  const focusRef = useRef(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState<BaseSavedSearch | null>(null);

  const fetchSavedSearch = () => {
    setIsLoading(true);

    return savedSearchService
      .getSavedSearch(savedSearchId)
      .then((savedSearch) => {
        setSearch(savedSearch);
        setIsError(false);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  };

  const openSettingsModal = () => {
    setIsSettingsOpen(true);
  };

  const closeSettingsModal = () => {
    setIsSettingsOpen(false);
    closeModal();
  };

  useEffect(() => {
    if (isOpen) {
      fetchSavedSearch().finally(openSettingsModal);
    }
  }, [isOpen]);

  const noSavedSearch = isError || !search;

  return noSavedSearch ? (
    <SavedSearchModal
      isOpen={isSettingsOpen}
      onDismiss={closeSettingsModal}
      focusRef={focusRef}
      body={
        isLoading ? <Loader /> : <SettingsError onRetry={fetchSavedSearch} />
      }
    />
  ) : (
    children(search, isSettingsOpen, closeSettingsModal)
  );
};
